/**
 * Service to reprocess asset facts based on checklist required fields
 * 
 * This service:
 * 1. Gets all checklists associated with an asset
 * 2. Collects all required fields from checklist items
 * 3. Reprocesses all uploaded files to extract those specific fields
 * 4. Updates asset facts with the extracted values
 */

import { assetService, Asset } from '@/firebase/assetService';
import { assetFileService, AssetFile } from '@/firebase/assetFileService';
import { assetFactsService } from '@/firebase/assetFactsService';
import { checklistService, Checklist } from '@/firebase/checklistService';
import { extractFieldValuesFromDocument, evaluateFieldWorkflowRuleWithAI } from '@/services/geminiService';
import { getFieldsForAssetType, AssetTypeEvidenceField } from '@/services/assetTypeFieldsService';
import { ref, getBytes } from 'firebase/storage';
import { storage } from '@/firebase/config';

export interface ReprocessAssetFactsResult {
  success: boolean;
  requiredFieldsCount?: number;
  checklistsCount?: number;
  errors?: string[];
}

export interface ReprocessSingleDocumentResult {
  success: boolean;
  factsExtracted: number;
  errors?: string[];
}

/**
 * Download a file from Firebase Storage and convert to File object
 * Uses assetFileService.downloadAssetFile() which handles Enterprise decryption automatically
 */
async function downloadFileFromStorage(assetFile: AssetFile, organizationId: string): Promise<File | null> {
  try {
    if (!assetFile.id) {
      console.warn(`File ${assetFile.fileName} missing ID`);
      return null;
    }

    // Use assetFileService.downloadAssetFile() which handles Enterprise decryption
    const downloadResult = await assetFileService.downloadAssetFile(assetFile.id, organizationId);
    
    if (!downloadResult.success || !downloadResult.data) {
      console.error(`Error downloading file ${assetFile.fileName}:`, downloadResult.error);
      return null;
    }

    return downloadResult.data;
  } catch (error: any) {
    console.error(`Error downloading file ${assetFile.fileName}:`, error);
    return null;
  }
}

/**
 * Collect all fields from evidenceDefinitions.fieldDefinitions across all checklist items
 * This is now the single source of truth for fields
 */
function collectRequiredFieldsFromChecklists(checklists: Checklist[]): Array<{
  key: string;
  label: string;
  type: 'date' | 'string' | 'enum' | 'number' | 'boolean' | 'list';
  extractionHints?: {
    keywords?: string[];
    anchors?: string[];
    regex?: string;
  };
}> {
  const fieldsMap = new Map<string, {
    key: string;
    label: string;
    type: 'date' | 'string' | 'enum' | 'number' | 'boolean' | 'list';
    extractionHints?: {
      keywords?: string[];
      anchors?: string[];
      regex?: string;
    };
  }>();

  for (const checklist of checklists) {
    if (!checklist.items || checklist.items.length === 0) continue;

    for (const item of checklist.items) {
      // Collect from evidenceDefinitions.fieldDefinitions (single source of truth)
      if (item.evidenceDefinitions && item.evidenceDefinitions.length > 0) {
        item.evidenceDefinitions.forEach(evidenceDef => {
          if (evidenceDef.fieldDefinitions && evidenceDef.fieldDefinitions.length > 0) {
            evidenceDef.fieldDefinitions.forEach(fieldDef => {
              // Only add if not already in map (avoid duplicates)
              if (!fieldsMap.has(fieldDef.key)) {
                fieldsMap.set(fieldDef.key, {
                  key: fieldDef.key,
                  label: fieldDef.label,
                  type: fieldDef.type,
                  extractionHints: fieldDef.extractionHints,
                });
              }
            });
          }
        });
      }
    }
  }

  return Array.from(fieldsMap.values());
}

/**
 * Get required fields for asset facts extraction based on checklist requirements
 * This only builds the field list - it does NOT process documents
 */
export interface RequiredFieldForExtraction {
  key: string;
  label: string;
  type: 'date' | 'string' | 'enum' | 'number' | 'boolean' | 'list';
  fileNameKey?: string;
  fileNameKeys?: string[];
  enumValues?: string[];
  workflowRules?: string;
  extractionHints?: {
    keywords?: string[];
    anchors?: string[];
    regex?: string;
  };
}

export async function getRequiredFieldsForAssetFacts(
  assetId: string,
  organizationId: string
): Promise<{
  success: boolean;
  requiredFields?: RequiredFieldForExtraction[];
  checklistsCount?: number;
  errors?: string[];
}> {
  try {
    // 1. Get the asset
    const assetResult = await assetService.getAsset(assetId);
    if (!assetResult.success || !assetResult.data) {
      return {
        success: false,
        errors: [`Failed to get asset: ${assetResult.error}`],
      };
    }

    const asset = assetResult.data;

    // 2. Get asset type fields (pre-aggregated from checklists)
    const assetTypeId = asset.assetTypeId;
    if (!assetTypeId) {
      return {
        success: false,
        errors: ['Asset has no assetTypeId'],
      };
    }

    // Always re-aggregate from checklists so field definitions (including type, e.g. string -> list) are up to date
    try {
      const { aggregateFieldsForAssetType } = await import('@/services/assetTypeFieldsService');
      const aggregateResult = await aggregateFieldsForAssetType(assetTypeId, organizationId);
      if (aggregateResult.success && (aggregateResult.fieldsCount ?? 0) > 0) {
        console.log(`[GetRequiredFieldsForAssetFacts] Re-aggregated ${aggregateResult.fieldsCount} fields for asset type ${assetTypeId}`);
      }
    } catch (aggregateError: any) {
      console.warn(`[GetRequiredFieldsForAssetFacts] Aggregation failed (using cached fields):`, aggregateError?.message);
    }

    const fieldsResult = await getFieldsForAssetType(assetTypeId);
    let assetTypeFields = fieldsResult.fields || [];
    
    if (assetTypeFields.length === 0) {
        // Still no fields - return error with helpful message
        // Check if we can provide more specific guidance
        let errorMessage = 'No fields found for this asset type. Please ensure:\n1. Checklists are linked to this asset type (check assetTypeIds in checklist settings)\n2. Checklists have evidence definitions with field definitions\n3. Edit and save the checklists to trigger field aggregation';
        
        // Try to get more context about why aggregation failed
        try {
          const { checklistService } = await import('@/firebase/checklistService');
          const checklistsResult = await checklistService.getAllChecklists(organizationId);
          if (checklistsResult.success && checklistsResult.data) {
            const linkedChecklists = checklistsResult.data.filter(c => 
              c.assetTypeIds && c.assetTypeIds.includes(assetTypeId)
            );
            if (linkedChecklists.length === 0) {
              errorMessage = `No checklists are linked to asset type "${assetTypeId}". Please link at least one checklist to this asset type in the checklist settings.`;
            } else {
              // Check if linked checklists have evidence fields
              const hasFields = linkedChecklists.some(c => 
                c.items?.some(item => 
                  item.evidenceDefinitions?.some(ev => 
                    ev.fieldDefinitions && ev.fieldDefinitions.length > 0
                  )
                )
              );
              if (!hasFields) {
                errorMessage = `${linkedChecklists.length} checklist(s) are linked to this asset type, but none have evidence definitions with field definitions. Please add evidence fields to your checklist items.`;
              }
            }
          }
        } catch (contextError) {
          // Ignore context errors, use default message
          console.error('Error getting context for error message:', contextError);
        }
        
        return {
          success: false,
          errors: [errorMessage],
        };
    }

    // Convert AssetTypeEvidenceField to format expected by extractFieldValuesFromDocument (include all metadata for extraction + workflow)
    const requiredFields: RequiredFieldForExtraction[] = assetTypeFields.map(field => ({
      key: field.key,
      label: field.label,
      type: field.type,
      fileNameKey: field.fileNameKey,
      fileNameKeys: field.fileNameKeys,
      enumValues: field.enumValues,
      workflowRules: field.workflowRules,
      extractionHints: field.extractionHints,
    }));

    console.log(`[GetRequiredFieldsForAssetFacts] Found ${requiredFields.length} fields from asset type ${assetTypeId}`);

    return {
      success: true,
      requiredFields,
      checklistsCount: 0, // No longer tracking checklist count since we use asset type fields
    };
  } catch (error: any) {
    console.error('Error getting required fields for asset facts:', error);
    return {
      success: false,
      errors: [`Unexpected error: ${error.message}`],
    };
  }
}

/**
 * Reprocess asset facts based on checklist required fields
 * NOTE: This function is kept for backward compatibility but is deprecated.
 * Use getRequiredFieldsForAssetFacts instead if you only need to build the field list.
 */
export async function reprocessAssetFacts(
  assetId: string,
  organizationId: string
): Promise<ReprocessAssetFactsResult> {
  const result: ReprocessAssetFactsResult = {
    success: true,
    requiredFieldsCount: 0,
    checklistsCount: 0,
    errors: [],
  };

  try {
    // Get required fields (this is the only thing we should do - build the prompt)
    const fieldsResult = await getRequiredFieldsForAssetFacts(assetId, organizationId);
    
    if (!fieldsResult.success || !fieldsResult.requiredFields) {
      result.success = false;
      result.errors = fieldsResult.errors || ['Failed to get required fields'];
      return result;
    }

    const requiredFields = fieldsResult.requiredFields;
    console.log(`[ReprocessAssetFacts] Built prompt with ${requiredFields.length} required fields from ${fieldsResult.checklistsCount} checklists`);

    // Return success - we've built the field list
    // The actual document processing should happen elsewhere (e.g., when files are uploaded)
    result.success = true;
    result.requiredFieldsCount = requiredFields.length;
    result.checklistsCount = fieldsResult.checklistsCount;
    return result;
  } catch (error: any) {
    console.error('Error reprocessing asset facts:', error);
    result.success = false;
    result.errors?.push(`Unexpected error: ${error.message}`);
    return result;
  }
}

/**
 * Reprocess a single document to extract asset facts based on checklist required fields
 */
export async function reprocessSingleDocument(
  assetId: string,
  fileId: string,
  organizationId: string
): Promise<ReprocessSingleDocumentResult> {
  const result: ReprocessSingleDocumentResult = {
    success: true,
    factsExtracted: 0,
    errors: [],
  };

  try {
    // 1. Get required fields for this asset
    const fieldsResult = await getRequiredFieldsForAssetFacts(assetId, organizationId);
    
    if (!fieldsResult.success || !fieldsResult.requiredFields) {
      result.success = false;
      result.errors = fieldsResult.errors || ['Failed to get required fields'];
      return result;
    }

    const requiredFields = fieldsResult.requiredFields;

    if (requiredFields.length === 0) {
      result.success = false;
      result.errors?.push('No required fields found in checklists for this asset. Cannot extract facts.');
      return result;
    }

    // 2. Get the specific file
    const filesResult = await assetFileService.getAssetFiles(assetId, organizationId);
    if (!filesResult.success || !filesResult.data) {
      result.success = false;
      result.errors?.push('Failed to get files');
      return result;
    }

    const assetFile = filesResult.data.find(f => f.id === fileId);
    if (!assetFile) {
      result.success = false;
      result.errors?.push('File not found');
      return result;
    }

    // Check if file is valid
    const fileUrl = assetFile.storageUrl || (assetFile as any).fileUrl;
    if (!fileUrl || assetFile.status === 'error') {
      result.success = false;
      result.errors?.push('File is not available for processing');
      return result;
    }

    // 3. Download file from storage (handles Enterprise decryption automatically)
    const file = await downloadFileFromStorage(assetFile, organizationId);
    if (!file) {
      result.success = false;
      result.errors?.push(`Failed to download file: ${assetFile.fileName}`);
      return result;
    }

    // 4. Extract field values only for fields without workflow rules (fields with workflow rules are derived from rules + asset facts only)
    const fieldsToExtract = requiredFields.filter(f => !f.workflowRules?.trim());
    const fieldResult = fieldsToExtract.length > 0
      ? await extractFieldValuesFromDocument(file, fieldsToExtract)
      : { fieldValues: {}, confidence: undefined as 'high' | 'medium' | 'low' | undefined };

    // Build factsToSave from extraction and/or workflow (all required fields can get workflow-derived values)
    const factsToSave: Record<string, { value: any; source: 'EXTRACTED'; extractedFrom?: string; confidence?: 'high' | 'medium' | 'low' }> = {};

    if (fieldResult.fieldValues && Object.keys(fieldResult.fieldValues).length > 0) {
      Object.entries(fieldResult.fieldValues).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          let valueToSave = value;
          const fieldDef = requiredFields.find(f => f.key === key);
          if (fieldDef?.type === 'list' && typeof value === 'string') {
            const trimmed = String(value).trim();
            if (trimmed.startsWith('[')) {
              try {
                const arr = JSON.parse(trimmed);
                valueToSave = Array.isArray(arr) ? arr.map((item: any) => String(item ?? '').trim()).filter(Boolean) : [trimmed];
              } catch {
                valueToSave = [trimmed];
              }
            } else {
              valueToSave = [trimmed];
            }
          }
          factsToSave[key] = {
            value: valueToSave,
            source: 'EXTRACTED',
            extractedFrom: assetFile.fileName,
            confidence: fieldResult.confidence,
          };
        }
      });
    }

    // 4.5. For fields with workflow rules, derive value (pattern or AI) and set/overwrite in factsToSave
    const existingFactsResult = await assetFactsService.getAssetFacts(assetId);
    const existingFacts = existingFactsResult.success && existingFactsResult.data?.facts ? existingFactsResult.data.facts : {};
    const mergedFacts: Record<string, any> = {};
    Object.entries(existingFacts).forEach(([k, v]) => {
      const factVal = (v as any).value ?? (v as any).extractedValues?.[0]?.value;
      mergedFacts[k] = { value: factVal };
    });
    Object.entries(factsToSave).forEach(([k, v]) => {
      mergedFacts[k] = { value: v.value };
    });
    for (const field of requiredFields) {
      if (!field.workflowRules?.trim()) continue;
      let derived: string | number | boolean | null | undefined;
      try {
        derived = await evaluateFieldWorkflowRuleWithAI(field.workflowRules, mergedFacts, {
          key: field.key,
          type: field.type,
          enumValues: field.enumValues,
        }) as string | number | boolean | null | undefined;
      } catch (e) {
        console.warn('[ReprocessSingleDocument] AI workflow evaluation failed for', field.key, e);
        derived = undefined;
      }
      if (derived !== undefined && derived !== null && derived !== '') {
        factsToSave[field.key] = {
          value: derived,
          source: 'EXTRACTED',
          extractedFrom: assetFile.fileName,
          confidence: 'high',
        };
        mergedFacts[field.key] = { value: derived };
      }
    }

    // 5. Update asset facts with extracted and workflow-derived values
    if (Object.keys(factsToSave).length > 0) {
      await assetFactsService.setFacts(assetId, factsToSave, organizationId);
      result.factsExtracted = Object.keys(factsToSave).length;
      console.log(`[ReprocessSingleDocument] Updated asset facts with ${result.factsExtracted} fields from ${assetFile.fileName}`);
    } else {
      result.success = true;
      result.factsExtracted = 0;
      result.errors?.push('Document processed but no valid field values were found');
    }

    return result;
  } catch (error: any) {
    console.error('Error reprocessing single document:', error);
    result.success = false;
    result.errors?.push(`Unexpected error: ${error.message}`);
    return result;
  }
}

